import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Plus, X, TrendingUp, TrendingDown, IndianRupee, ArrowUpRight, ArrowDownRight, Trash2, Pencil, Save, Search, RefreshCw, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCan } from '../hooks/useCan';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// iter218 — Static fallback if the dynamic categories endpoint doesn't load.
// The actual category dropdown is populated from /api/cashflow/categories so
// every real category in the database (Vendor Payment, Sales Receipt,
// expense:salary, Loan Principal, …) is selectable. The hardcoded 7-item
// list previously hid 80%+ of entries when a filter was applied.
const FALLBACK_CATEGORIES = ['Sales', 'Sales Receipt', 'Receipt', 'Purchase', 'Vendor Payment', 'Raw Material Purchase', 'COGS', 'Salary', 'Operations', 'Taxes', 'Loan', 'Loan Principal', 'Loan Interest', 'Overdraft Draw', 'Interest Income', 'Opening Balance Settlement', 'Other'];

const GROUP_STYLE = {
  all:              { label: 'All',                 bg: 'bg-amber-500', text: 'text-black' },
  sales_receipts:   { label: 'Sales & Receipts',    bg: 'bg-emerald-600/30',  text: 'text-emerald-300', border: 'border-emerald-700' },
  vendor_purchases: { label: 'Vendor & Purchases',  bg: 'bg-rose-600/30',     text: 'text-rose-300',    border: 'border-rose-700' },
  expenses_opex:    { label: 'Expenses & Opex',     bg: 'bg-blue-600/30',     text: 'text-blue-300',    border: 'border-blue-700' },
  loans_finance:    { label: 'Loans & Finance',     bg: 'bg-purple-600/30',   text: 'text-purple-300',  border: 'border-purple-700' },
  opening_balances: { label: 'Opening Balances',    bg: 'bg-cyan-600/30',     text: 'text-cyan-300',    border: 'border-cyan-700' },
  other:            { label: 'Other',               bg: 'bg-zinc-700',        text: 'text-zinc-300',    border: 'border-zinc-600' },
};

const PAGE_SIZE = 50;

export default function Cashflow() {
  const { user } = useAuth();
  const can = useCan();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const canDelete = isAdmin || can('can_delete_transactions');
  const canEdit = isAdmin || user?.role === 'manager' || can('can_edit_transactions');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoriesCatalog, setCategoriesCatalog] = useState([]);  // iter218 — dynamic categories from API
  const [groupCatalog, setGroupCatalog] = useState([]);            // iter218 — canonical group catalog
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');           // iter218 — category group pills
  const [search, setSearch] = useState('');                        // iter217 — search by description/notes/category
  const [page, setPage] = useState(1);                             // iter218 — pagination (was hardcoded slice(0,50))
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ type: 'inflow', amount: '', description: '', category: 'Sales', date: new Date().toISOString().slice(0, 10), gst_amount: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);  // iter181 — edit modal state

  const fetchData = async () => {
    try {
      const [txRes, sumRes, catRes] = await Promise.all([
        axios.get(`${API}/cashflow/transactions`, { withCredentials: true }),
        axios.get(`${API}/cashflow/summary`, { withCredentials: true }),
        axios.get(`${API}/cashflow/categories`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setTransactions(txRes.data);
      setSummary(sumRes.data);
      if (catRes.data) {
        setCategoriesCatalog(catRes.data.categories || []);
        setGroupCatalog(catRes.data.groups || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // iter184 — human-readable cascade descriptions for the confirm dialog
  const CASCADE_HINTS = {
    purchase_payment:           '↪ The linked vendor bill will revert to Unpaid/Partial.',
    sales_order_payment:        '↪ The linked customer invoice will revert to Unpaid/Partial (ledger + voucher synced).',
    voucher_payment:            '↪ The linked voucher will revert to Unpaid.',
    purchase_order_payment:     '↪ The linked PO will revert to Unpaid/Partial.',
    loan_payment:               '↪ The loan payment row will be dropped and the loan re-opened if it was auto-settled.',
    od_transaction:             '↪ The linked OD draw/repay will be reversed (utilization restored).',
    cc_transaction:             '↪ The linked credit-card transaction will be removed (balance restored).',
    bank_reco_payment:          '↪ The bill payment will be removed and the bank statement line reset to "unmatched".',
    bank_reco_receipt:          '↪ The customer payment will be removed and the bank statement line reset to "unmatched".',
    opening_balance_settlement: '↪ The opening balance will be restored to the original carry-forward amount.',
    loan:                       '↪ The loan inflow stamp will be cleared (loan itself remains).',
    bank_reco_classify:         '↪ This is a standalone classified expense/income; no other records will change.',
  };

  const handleDelete = async (t) => {
    const hint = CASCADE_HINTS[t.reference_type];
    const linkedNote = t.reference_type
      ? `\n\nThis entry is linked to: ${t.reference_type.replace(/_/g, ' ')}.${hint ? '\n' + hint : ''}`
      : '';
    if (!window.confirm(`Delete "${t.description}" — ${fmt(t.amount)}?${linkedNote}`)) return;
    try {
      const res = await axios.delete(`${API}/cashflow/transactions/${t.id}`, { withCredentials: true });
      setTransactions(prev => prev.filter(x => x.id !== t.id));
      // Re-fetch summary so dashboard / monthly chart stay in sync
      const sumRes = await axios.get(`${API}/cashflow/summary`, { withCredentials: true });
      setSummary(sumRes.data);
      const reverted = res.data?.reverted || {};
      const keys = Object.entries(reverted).filter(([, v]) => v).map(([k]) => k.replace(/_/g, ' '));
      toast.success(`Deleted${keys.length ? ` · reversed ${keys.join(', ')}` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/cashflow/transactions`, { ...form, amount: parseFloat(form.amount), gst_amount: parseFloat(form.gst_amount || 0) }, { withCredentials: true });
      setTransactions(prev => [res.data, ...prev]);
      setSummary(prev => prev ? {
        ...prev,
        total_inflow: prev.total_inflow + (form.type === 'inflow' ? parseFloat(form.amount) : 0),
        total_outflow: prev.total_outflow + (form.type === 'outflow' ? parseFloat(form.amount) : 0),
        balance: prev.balance + (form.type === 'inflow' ? parseFloat(form.amount) : -parseFloat(form.amount)),
      } : prev);
      setShowModal(false);
      setForm({ type: 'inflow', amount: '', description: '', category: 'Sales', date: new Date().toISOString().slice(0, 10), gst_amount: 0 });
      toast.success('Transaction recorded!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  // iter218 — filter pipeline: type → group → category → search
  const filtered = useMemo(() => transactions.filter(t => {
    if (filter !== 'all' && t.type !== filter) return false;
    if (groupFilter !== 'all' && (t._group || 'other') !== groupFilter) return false;
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      const hay = `${t.description || ''} ${t.category || ''} ${t.reference_type || ''} ${t.notes || ''} ${t.party_name || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [transactions, filter, groupFilter, categoryFilter, search]);

  // Reset to page 1 when any filter changes so user doesn't sit on an empty page.
  useEffect(() => { setPage(1); }, [filter, groupFilter, categoryFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Counts per group for the pill bar
  const groupCounts = useMemo(() => {
    const c = { all: transactions.length };
    for (const t of transactions) {
      const g = t._group || 'other';
      c[g] = (c[g] || 0) + 1;
    }
    return c;
  }, [transactions]);

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading cashflow...</div>;

  return (
    <div className="space-y-5" data-testid="cashflow-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Cashflow Management</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            {transactions.length} transactions recorded
            {filtered.length !== transactions.length && (
              <span className="text-amber-400 ml-2">· {filtered.length} match filter</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="add-transaction-btn" onClick={() => setShowModal(true)} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            <Plus size={14} /> Add Transaction
          </button>
        </div>
      </div>

      {/* iter221 — Reconcile + Cleanup Dupes buttons + diagnostics banner
          REMOVED per user request. All rows with reconciled:true were
          auto-purged from db.transactions at boot (see server.py startup). */}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Inflow', value: fmt(summary?.total_inflow), icon: ArrowUpRight, color: 'text-green-400', bg: 'bg-green-500/10', sub: `This month: ${fmt(summary?.month_inflow)}` },
          { label: 'Total Outflow', value: fmt(summary?.total_outflow), icon: ArrowDownRight, color: 'text-red-400', bg: 'bg-red-500/10', sub: `This month: ${fmt(summary?.month_outflow)}` },
          { label: 'Net Balance', value: fmt(summary?.balance), icon: IndianRupee, color: summary?.balance >= 0 ? 'text-amber-400' : 'text-red-400', bg: summary?.balance >= 0 ? 'bg-amber-500/10' : 'bg-red-500/10', sub: summary?.balance >= 0 ? 'Positive cashflow' : 'Negative cashflow' },
        ].map(({ label, value, icon: Icon, color, bg, sub }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid={`cf-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-zinc-400 text-xs">{label}</span>
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}><Icon size={15} className={color} /></div>
            </div>
            <div className={`font-mono font-bold text-2xl font-heading ${color}`}>{value}</div>
            <div className="text-zinc-500 text-xs mt-1">{sub}</div>
          </div>
        ))}
      </div>

      {/* Monthly Chart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4">Monthly Cashflow Comparison</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={summary?.monthly_chart || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="month" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '12px' }} formatter={(v) => [fmt(v)]} />
            <Legend wrapperStyle={{ fontSize: '11px', color: '#71717A' }} />
            <Bar dataKey="inflow" fill="#22C55E" radius={[4, 4, 0, 0]} name="Inflow" />
            <Bar dataKey="outflow" fill="#EF4444" radius={[4, 4, 0, 0]} name="Outflow" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Filters + Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {/* iter218 — Category group pills: makes every entry reachable by
            grouping the 18+ raw category strings into 6 logical buckets. */}
        <div className="flex flex-wrap gap-1.5 px-4 pt-4" data-testid="cashflow-group-pills">
          {['all', ...groupCatalog.map(g => g.slug)].map(slug => {
            const style = GROUP_STYLE[slug] || GROUP_STYLE.other;
            const label = slug === 'all' ? 'All' : (groupCatalog.find(g => g.slug === slug)?.label || slug);
            const count = groupCounts[slug] || 0;
            const isActive = groupFilter === slug;
            return (
              <button key={slug} data-testid={`group-${slug}`}
                onClick={() => setGroupFilter(slug)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${isActive ? `${style.bg} ${style.text} ${style.border || 'border-transparent'}` : 'bg-zinc-800/50 text-zinc-400 border-zinc-700 hover:bg-zinc-800'}`}>
                {label} <span className="ml-1 opacity-75 font-mono">({count})</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 p-4 border-b border-zinc-800">
          <div className="flex gap-1 bg-zinc-800 rounded-xl p-1">
            {['all', 'inflow', 'outflow'].map(f => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${filter === f ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>{f}</button>
            ))}
          </div>
          {/* iter218 — Dynamic dropdown from /api/cashflow/categories so EVERY actual category in the DB is selectable (was hardcoded to 7). */}
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-testid="cashflow-category-filter"
            className="bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-1.5 text-zinc-300 text-xs focus:outline-none focus:border-amber-500 max-w-[260px]">
            <option value="all">All Categories ({transactions.length})</option>
            {(categoriesCatalog.length > 0 ? categoriesCatalog : FALLBACK_CATEGORIES.map(c => ({ category: c, count: 0 }))).map(c => (
              <option key={c.category} value={c.category} className="bg-zinc-900">
                {c.category}{c.count ? ` (${c.count})` : ''}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-1.5 flex-1 min-w-[200px]">
            <Search size={13} className="text-zinc-500" />
            <input
              data-testid="cashflow-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by description, party, category, notes…"
              className="bg-transparent flex-1 text-zinc-50 text-xs focus:outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-zinc-500 hover:text-zinc-300" title="Clear">
                <X size={12} />
              </button>
            )}
          </div>
          <span className="text-zinc-500 text-xs self-center">
            {filtered.length === 0 ? '0' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)}`} of {filtered.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="border-b border-zinc-800">
              {['Date', 'Description', 'Category', 'Type', 'GST', 'Amount'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
              {(canEdit || canDelete) && <th className="text-right text-zinc-500 text-xs font-medium px-4 py-3"></th>}
            </tr></thead>
            <tbody>
              {pageRows.map(t => {
                const gs = GROUP_STYLE[t._group] || GROUP_STYLE.other;
                return (
                <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`txn-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-zinc-500 text-xs whitespace-nowrap">{(t.date || '').slice(0, 10)}</td>
                  <td className="px-4 py-3 text-zinc-300 text-xs max-w-md">
                    <div className="truncate" title={t.description}>{t.description}</div>
                    {t.reference_type && (
                      <div className="text-[9px] text-zinc-600 mt-0.5 font-mono">{t.reference_type.replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${gs.text} ${gs.border || 'border-zinc-700'} bg-zinc-950 inline-block w-fit`}>
                      {t.category || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.type === 'inflow' ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-red-900/40 text-red-300 border border-red-700'}`}>
                      {t.type === 'inflow' ? '↑ Inflow' : '↓ Outflow'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{t.gst_amount > 0 ? fmt(t.gst_amount) : '—'}</td>
                  <td className={`px-4 py-3 font-mono text-sm font-semibold ${t.type === 'inflow' ? 'text-green-400' : 'text-red-400'}`}>
                    {t.type === 'inflow' ? '+' : '-'}{fmt(t.amount)}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {canEdit && (
                          <button
                            data-testid={`edit-txn-${t.id}`}
                            onClick={() => setEditingTxn(t)}
                            className="p-1.5 rounded-md bg-zinc-800/60 hover:bg-amber-900/50 text-zinc-400 hover:text-amber-300 border border-zinc-700 transition-colors"
                            title={t.reference_type && t.reference_type !== 'manual'
                              ? `Edit · cascade ${t.reference_type.replace('_', ' ')}`
                              : 'Edit this entry'}>
                            <Pencil size={12} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            data-testid={`delete-txn-${t.id}`}
                            onClick={() => handleDelete(t)}
                            className="p-1.5 rounded-md bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 border border-red-800/30 transition-colors"
                            title={t.reference_type && t.reference_id
                              ? `Delete & revert linked ${t.reference_type.replace('_', ' ')}`
                              : 'Delete this entry'}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="text-center text-zinc-500 text-sm py-8" data-testid="cashflow-empty">No transactions match the current filter</div>}
        {/* iter218 — Pagination footer (was: hardcoded slice(0, 50) which
            hid every entry past row 50 — the user-reported "I can't see all
            entries" bug). */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800" data-testid="cashflow-pagination">
            <div className="text-zinc-500 text-xs">Page {page} of {totalPages}</div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev"
                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 border border-zinc-700">
                <ChevronLeft size={13} className="text-zinc-400" />
              </button>
              <span className="text-zinc-400 text-xs px-3 font-mono">{page}/{totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="page-next"
                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 border border-zinc-700">
                <ChevronRight size={13} className="text-zinc-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">Add Transaction</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Type</label>
                <div className="flex gap-2">
                  {['inflow', 'outflow'].map(t => (
                    <button key={t} type="button" onClick={() => setForm(f => ({ ...f, type: t }))} className={`flex-1 py-2 rounded-xl text-xs font-medium capitalize transition-all ${form.type === t ? (t === 'inflow' ? 'bg-green-600 text-white' : 'bg-red-600 text-white') : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>{t}</button>
                  ))}
                </div>
              </div>
              {[['description', 'Description *', 'text'], ['amount', 'Amount (₹) *', 'number'], ['gst_amount', 'GST Amount (₹)', 'number']].map(([field, label, type]) => (
                <div key={field}>
                  <label className="text-zinc-400 text-xs mb-1 block">{label}</label>
                  <input required={field !== 'gst_amount'} type={type} step="any" value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              ))}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                  {(categoriesCatalog.length > 0 ? categoriesCatalog.map(c => c.category) : FALLBACK_CATEGORIES).map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="submit-txn-btn" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* iter181 — Edit Modal */}
      {editingTxn && (
        <EditTxnModal
          txn={editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={async () => {
            setEditingTxn(null);
            await fetchData();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  iter181 — Edit Transaction modal (cascades to linked source)
// ─────────────────────────────────────────────────────────────────────────
function EditTxnModal({ txn, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount:      String(txn.amount || ''),
    description: txn.description || '',
    category:    txn.category || 'Other',
    type:        txn.type || 'outflow',
    date:        (txn.date || '').slice(0, 10),
    gst_amount:  String(txn.gst_amount || 0),
  });
  const [busy, setBusy] = useState(false);
  const isLinked = txn.reference_type && txn.reference_type !== 'manual';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category,
        type: form.type,
        date: form.date,
        gst_amount: parseFloat(form.gst_amount || 0),
      };
      const r = await axios.put(`${API}/cashflow/transactions/${txn.id}`, payload, { withCredentials: true });
      const cascaded = r.data?.cascaded || {};
      const cascadedKeys = Object.keys(cascaded).filter(k => cascaded[k]);
      toast.success(
        `Saved · ${cascadedKeys.length ? 'cascaded to ' + cascadedKeys.join(', ') : 'no linked changes'}`
      );
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="edit-txn-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-zinc-50 font-semibold text-base flex items-center gap-2">
            <Pencil size={16} className="text-amber-400" /> Edit Transaction
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18}/></button>
        </div>

        {isLinked && (
          <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-2.5 mb-3 text-xs text-amber-300">
            ⚠️ Linked to <span className="font-mono">{txn.reference_type.replace('_', ' ')}</span>. Amount changes will cascade to the source record (bill / loan / OD / etc.).
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Type</label>
              <div className="flex gap-1">
                {['inflow', 'outflow'].map(tt => (
                  <button key={tt} type="button"
                    onClick={() => setForm(f => ({ ...f, type: tt }))}
                    data-testid={`edit-type-${tt}`}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize ${
                      form.type === tt
                        ? (tt === 'inflow' ? 'bg-green-600 text-white' : 'bg-red-600 text-white')
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}>{tt}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Amount ₹</label>
              <input type="number" required step="any" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="edit-amount-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"/>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Description</label>
            <input value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              data-testid="edit-description-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Category</label>
              <input value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                data-testid="edit-category-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm"/>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Date</label>
              <input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm"/>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">GST Amount</label>
            <input type="number" step="any" value={form.gst_amount}
              onChange={e => setForm(f => ({ ...f, gst_amount: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm"/>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="edit-save-btn"
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
              <Save size={13}/> {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
